import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import type { DocumentNode } from '@apollo/client';
import { useToast } from '../../components/Toast/ToastContext';
import {
  rescueChipStatsQuery,
  RescueChipStatsData,
  rescueDeviceStatsQuery,
  RescueDeviceStatsData,
  rescueGwaDashboardStatsQuery,
  RescueGwaDashboardStatsData,
  rescueDevicesProgramQuery,
  RescueDevicesProgramData,
  rescuePetsQuery,
  RescuePetsData,
  RescuePetGql,
  rescueDevicesQuery,
  RescueDevicesData,
  removePetMutation,
  rescueCreatePetAdoptionTransferMutation,
  rescueCreatePetFosterTransferMutation,
  rescueRecallPetFosterTransferMutation,
  unassignDeviceMutation,
} from '../../lib/rescueQueries';
import {
  recallChipMutation,
  reassignChipToResellerMutation,
} from '@/appview/ResellerChipDetails/ResellerChipDetails.graphql';
import { assignNewOwnerToChipMutation } from '@/appview/RegisterChipV2/RegisterChip.graphql';
import SearchBar from '../../components/SearchBar/SearchBar';
import Button from '../../components/Button/Button';
import PetsTable, { type PetRow } from './PetsTable';
import RemovePetModal from './RemovePetModal';
import AssignDeviceModal from './AssignDeviceModal';
import AssignDeviceToPetModal from './AssignDeviceToPetModal';
import AddChipToPetModal from './AddChipToPetModal';
import ViewPetModal from './ViewPetModal';
import PetFormModal from './PetFormModal';
import TransferToAdopterModal from './TransferToAdopterModal';
import TransferToFosterModal from './TransferToFosterModal';
import TransferChipModal, { type AdopterFormData } from '../Chips/TransferChipModal';
import { Chip } from '../../lib/types';
import { mapAdopterFormToContactInfoInput } from '../../lib/chipAdapters';
import {
  trackDashboardViewed,
  trackActionItemClicked,
  trackPetViewed,
  trackPetRemoved,
  trackDeviceTransferToFoster,
  trackDeviceTransferToAdopt,
  trackDeviceRecalled,
  trackChipOwnerAdded,
} from '../../analytics';
import styles from './Dashboard.module.scss';

type Urgency = 'warning' | 'error' | 'info';

const URGENCY_COLORS: Record<Urgency, { bg: string; text: string }> = {
  error: { bg: '#fbefee', text: '#e0352a' },
  warning: { bg: '#fffcbd', text: '#b8860b' },
  info: { bg: '#e1f0ff', text: '#1360f4' },
};

function getT2aSubtext(pending: number, completed: number) {
  const total = completed + pending;
  if (total === 0) return { text: 'No transfers yet', color: '' };
  const rate = completed / total;
  const pct = Math.round(rate * 100);
  let color: string;
  if (rate >= 0.8) color = 'var(--success-text, #1B7D3A)';
  else if (rate >= 0.6) color = 'var(--warning-text, #B8860B)';
  else color = 'var(--error-text, #E0352A)';
  return {
    text: `T2A rate: ${pct}% (${completed} completed, ${pending} pending)`,
    color,
  };
}

interface ActionItem {
  title: string;
  description: string;
  link: string;
  urgency: Urgency;
}

/**
 * Tri-state content for the GWA Redemptions card on the Dashboard.
 * Loading shows a neutral placeholder so we don't flash the "Not enrolled" CTA
 * before the query resolves.
 */
function getGwaCardContent(loading: boolean, data: RescueGwaDashboardStatsData | undefined) {
  if (loading && !data) {
    return { value: '\u2014' as string | number, subtext: null as string | null };
  }
  const program = data?.rescueGwaProgram ?? null;
  if (!program) {
    return { value: '\u2014' as string | number, subtext: 'Set up your program \u203A' };
  }
  return {
    value: program.monthRedemptions,
    subtext: `last 30 days \u00B7 ${program.totalRedemptions} total`,
  };
}

/**
 * Tri-state content for the GPS Devices card. Mirrors the GWA card: loading
 * shows a neutral placeholder, unenrolled prompts the user to set up, enrolled
 * shows the active device count + T2A subtext.
 */
function getDevicesCardContent(
  loading: boolean,
  data: RescueDevicesProgramData | undefined,
  activeDevices: number,
  t2a: { text: string; color: string | null },
) {
  if (loading && !data) {
    return { value: '\u2014' as string | number, subtext: null as string | null, subtextColor: null as string | null };
  }
  if (!data?.rescueDevicesProgram?.enrolled) {
    return {
      value: '\u2014' as string | number,
      subtext: 'Set up your program \u203A',
      subtextColor: null as string | null,
    };
  }
  return { value: activeDevices, subtext: t2a.text, subtextColor: t2a.color };
}

function buildActionItems(pending: number, completed: number): ActionItem[] {
  const items: ActionItem[] = [];
  const total = completed + pending;
  if (total > 0 && pending > 0) {
    const rate = completed / total;
    const pct = Math.round(rate * 100);
    const urgency: Urgency = rate < 0.6 ? 'error' : rate < 0.8 ? 'warning' : 'info';
    items.push({
      title: `T2A acceptance rate is ${pct}%`,
      description: `${pending} adopter${
        pending === 1 ? ' hasn\u2019t' : 's haven\u2019t'
      } completed setup \u2014 consider resending setup emails`,
      link: '/rescue/devices',
      urgency,
    });
  }
  return items;
}

function useModal<T = void>() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<T | null>(null);
  return {
    open,
    data,
    show(value?: T) {
      setData(value ?? null);
      setOpen(true);
    },
    close() {
      setOpen(false);
      setData(null);
    },
  };
}

const ALL_DEVICES_PAGE_SIZE = 100;

/**
 * Fetches all pages of a paginated rescue devices query.
 * Uses cache-first so repeated renders don't re-fetch, and
 * fetchMore to accumulate pages until hasMore is false.
 */
const MAX_FETCH_MORE_RETRIES = 3;

type DeviceItem = RescueDevicesData['rescueDevices']['items'][number];

function useAllDeviceItems(query: DocumentNode, filter: string) {
  const { data, loading, fetchMore } = useQuery<RescueDevicesData>(query, {
    variables: { input: { limit: ALL_DEVICES_PAGE_SIZE, offset: 0, filter } },
    fetchPolicy: 'cache-first',
  });

  const [extraItems, setExtraItems] = useState<DeviceItem[]>([]);
  const paginating = useRef(false);
  const retries = useRef(0);
  const firstPageItems = data?.rescueDevices?.items ?? [];
  const hasMore = data?.rescueDevices?.hasMore ?? false;
  const items = extraItems.length > 0 ? [...firstPageItems, ...extraItems] : firstPageItems;

  // Reset accumulated pages when the first page is refetched (e.g. after a mutation)
  useEffect(() => {
    setExtraItems([]);
    retries.current = 0;
  }, [data]);

  useEffect(() => {
    if (!hasMore || loading || paginating.current || retries.current >= MAX_FETCH_MORE_RETRIES) {
      return;
    }
    let cancelled = false;
    paginating.current = true;
    fetchMore({
      variables: { input: { limit: ALL_DEVICES_PAGE_SIZE, offset: items.length, filter } },
    })
      .then(({ data: result }) => {
        if (!cancelled && result?.rescueDevices?.items?.length) {
          setExtraItems((prev) => [...prev, ...result.rescueDevices.items]);
          retries.current = 0;
        }
      })
      .catch(() => {
        if (!cancelled) {
          retries.current += 1;
        }
      })
      .finally(() => {
        paginating.current = false;
      });
    return () => {
      cancelled = true;
    };
  }, [hasMore, loading, items.length, filter, fetchMore]);

  return { items, loading: loading || (hasMore && retries.current < MAX_FETCH_MORE_RETRIES) };
}

export default function Dashboard() {
  const history = useHistory();
  const { showToast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  // Chip counts — use the dedicated stats resolver which runs efficient
  // parallel COUNT queries instead of the paginated chips query whose
  // getManyAndCount() can timeout for large rescues.
  const { data: chipStatsData } = useQuery<RescueChipStatsData>(rescueChipStatsQuery, {
    fetchPolicy: 'cache-and-network',
  });

  const chipStock = chipStatsData?.currentUser?.chipReseller?.stats?.totalStock ?? 0;
  const chipRegistered = chipStatsData?.currentUser?.chipReseller?.stats?.totalRegistered ?? 0;

  // Device / T2A stats
  const { data: deviceStatsData } = useQuery<RescueDeviceStatsData>(rescueDeviceStatsQuery, {
    fetchPolicy: 'cache-and-network',
  });
  const deviceStats = deviceStatsData?.rescueDeviceStats;
  const activeDevices = deviceStats?.totalActiveDevices ?? 0;
  const pendingCount = deviceStats?.pendingAdoptionsCount ?? 0;
  const completedCount = deviceStats?.completedAdoptionsCount ?? 0;
  const t2a = getT2aSubtext(pendingCount, completedCount);
  const actionItems = buildActionItems(pendingCount, completedCount);

  // GWA enrollment + redemption counts
  const { data: gwaStatsData, loading: gwaStatsLoading } = useQuery<RescueGwaDashboardStatsData>(
    rescueGwaDashboardStatsQuery,
    { fetchPolicy: 'cache-and-network' },
  );
  const gwaCard = getGwaCardContent(gwaStatsLoading, gwaStatsData);

  // Devices program enrollment — drives the GPS Devices card's CTA state.
  const { data: devicesEnrollmentData, loading: devicesEnrollmentLoading } = useQuery<RescueDevicesProgramData>(
    rescueDevicesProgramQuery,
    { fetchPolicy: 'cache-and-network' },
  );
  const devicesCard = getDevicesCardContent(devicesEnrollmentLoading, devicesEnrollmentData, activeDevices, t2a);

  // ── Unassigned pets ──────────────────────────

  const { data: petsData, loading: petsLoading } = useQuery<RescuePetsData>(rescuePetsQuery);

  // Fetch all device lists per category to derive pet status.
  // Uses cache-first + pagination so we don't miss devices beyond page 1.
  const foster = useAllDeviceItems(rescueDevicesQuery, 'IN_FOSTER');
  const pendingDevices = useAllDeviceItems(rescueDevicesQuery, 'PENDING_ADOPTIONS');
  const completedDevices = useAllDeviceItems(rescueDevicesQuery, 'COMPLETED_ADOPTIONS');

  type DeviceStatusEntry = {
    status: 'foster' | 'pending_adoption' | 'adopted';
    updatedAt?: string | null;
    pet: RescueDevicesData['rescueDevices']['items'][number]['pet'];
  };

  const deviceStatusEntries = useMemo(() => {
    const entries: DeviceStatusEntry[] = [];
    for (const item of foster.items) {
      entries.push({
        status: 'foster',
        updatedAt: item.transfer?.updatedAt ?? item.transfer?.createdAt ?? null,
        pet: item.pet,
      });
    }
    for (const item of pendingDevices.items) {
      entries.push({
        status: 'pending_adoption',
        updatedAt: item.transfer?.updatedAt ?? item.transfer?.createdAt ?? null,
        pet: item.pet,
      });
    }
    for (const item of completedDevices.items) {
      entries.push({
        status: 'adopted',
        updatedAt: item.transfer?.completedAt ?? item.transfer?.updatedAt ?? null,
        pet: item.pet,
      });
    }
    return entries;
  }, [foster.items, pendingDevices.items, completedDevices.items]);

  const allPets = useMemo(() => {
    const rescueHousehold = petsData?.currentUser?.userHouseholds?.find((uh) => uh.household.type === 'RESCUE');
    const householdPets = rescueHousehold?.household.pets ?? [];

    // Build a map of pet ID → status from device queries
    const statusMap = new Map<string, DeviceStatusEntry>();
    for (const entry of deviceStatusEntries) {
      statusMap.set(entry.pet.id, entry);
    }

    // Start with household pets, applying status overrides
    const seenIds = new Set<string>();
    const result = householdPets.map((pet) => {
      seenIds.add(pet.id);
      const statusEntry = statusMap.get(pet.id);
      return {
        ...pet,
        hasDevice: !!pet.device,
        hasChip: !!pet.chip,
        status: statusEntry?.status ?? ('rescue' as const),
        updatedAt: statusEntry?.updatedAt ?? pet.updatedAt ?? null,
      };
    });

    // Add foster/pending/adopted pets not in the rescue household
    for (const entry of deviceStatusEntries) {
      if (!seenIds.has(entry.pet.id)) {
        seenIds.add(entry.pet.id);
        const p = entry.pet;
        result.push({
          id: p.id,
          name: p.name,
          gender: p.gender ?? null,
          species: p.species ?? null,
          weight: p.weight ?? null,
          color: p.color ?? null,
          additionalInfo: p.additionalInfo ?? null,
          yearOfBirth: p.yearOfBirth ?? null,
          monthOfBirth: p.monthOfBirth ?? null,
          dayOfBirth: p.dayOfBirth ?? null,
          breed: p.breed ?? null,
          chip: p.chip ?? null,
          device: p.device ? { id: p.device.id, moduleId: p.device.moduleId } : null,
          updatedAt: entry.updatedAt ?? null,
          hasDevice: !!p.device,
          hasChip: !!p.chip,
          status: entry.status,
        });
      }
    }

    return result;
  }, [petsData, deviceStatusEntries]);

  const filteredPets = useMemo(() => {
    let result = allPets;
    if (statusFilter === 'rescue') {
      result = result.filter((pet) => pet.status === 'rescue');
    } else if (statusFilter === 'foster') {
      result = result.filter((pet) => pet.status === 'foster');
    } else if (statusFilter === 'adopted') {
      result = result.filter((pet) => pet.status === 'pending_adoption' || pet.status === 'adopted');
    } else if (statusFilter === 'no_device') {
      result = result.filter((pet) => !pet.hasDevice);
    }
    if (searchQuery) {
      const lower = searchQuery.toLowerCase();
      result = result.filter(
        (pet) => pet.name.toLowerCase().includes(lower) || (pet.breed?.name ?? '').toLowerCase().includes(lower),
      );
    }
    return result;
  }, [allPets, searchQuery, statusFilter]);

  const mutationOpts = { refetchQueries: 'active' as const };
  const [removePetMut] = useMutation(removePetMutation, mutationOpts);
  const [createAdoptionTransferMut] = useMutation(rescueCreatePetAdoptionTransferMutation, mutationOpts);
  const [createFosterTransferMut] = useMutation(rescueCreatePetFosterTransferMutation, mutationOpts);
  const [recallFosterTransferMut] = useMutation(rescueRecallPetFosterTransferMutation, mutationOpts);
  const [unassignDeviceMut] = useMutation(unassignDeviceMutation, mutationOpts);
  const [recallChipMut] = useMutation(recallChipMutation, mutationOpts);
  const [reassignToReseller] = useMutation(reassignChipToResellerMutation, mutationOpts);
  const [assignNewOwner] = useMutation(assignNewOwnerToChipMutation, mutationOpts);

  const removePetModal = useModal<{
    id: string;
    name: string;
    species?: string | null;
    breed?: { name: string } | null;
    chip?: { id: string } | null;
    device?: { id: string } | null;
  }>();
  const fosterModal = useModal<{ moduleId: string; petId: string }>();
  const assignDeviceModal = useModal<string>();
  const viewPetModal = useModal<string>();
  const createPetModal = useModal();
  const editPetModal = useModal<string>();
  const addChipModal = useModal<{ id: string; name: string; breed?: { name: string } | null }>();
  const viewPetTarget = viewPetModal.data ? (allPets.find((p) => p.id === viewPetModal.data) ?? null) : null;
  const editPetTarget = editPetModal.data ? (allPets.find((p) => p.id === editPetModal.data) ?? null) : null;
  const assignToPetModal = useModal<RescuePetGql>();
  const adopterModal = useModal<{ moduleId: string; petId: string }>();
  const addOwnerModal = useModal<Chip>();

  function handleOpenRemovePet(petId: string, petName: string) {
    const pet = allPets.find((p) => p.id === petId);
    removePetModal.show({
      id: petId,
      name: petName,
      species: pet?.species ?? null,
      breed: pet?.breed ?? null,
      chip: pet?.chip ? { id: pet.chip.id } : null,
      device: pet?.device ? { id: pet.device.id } : null,
    });
  }

  async function handleConfirmRemovePet(petId: string, petName: string) {
    const pet = allPets.find((p) => p.id === petId);
    const hadChip = !!pet?.chip;
    const hadDevice = !!pet?.device;
    try {
      await removePetMut({ variables: { subjectId: petId } });
      trackPetRemoved(hadChip, hadDevice);
      showToast(`${petName} removed`);
    } catch (err) {
      showToast(`Failed to remove pet: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  function handleViewPet(pet: PetRow) {
    trackPetViewed(!!pet.chip, !!pet.device, pet.status ?? 'rescue', pet.species ?? undefined);
    viewPetModal.show(pet.id);
  }

  function handleEditPet(petId: string) {
    editPetModal.show(petId);
  }

  function handleOpenAddChip(petId: string) {
    const pet = allPets.find((p) => p.id === petId) ?? viewPetTarget;
    if (pet) {
      addChipModal.show(pet);
    }
  }

  function handleOpenAssignDevice(petId: string) {
    const pet = allPets.find((p) => p.id === petId);
    if (pet) {
      assignToPetModal.show(pet);
    } else {
      assignDeviceModal.show(petId);
    }
  }

  async function handleTransferToAdopter(
    petId: string,
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
  ) {
    try {
      await createAdoptionTransferMut({
        variables: {
          input: {
            petId,
            firstName,
            lastName: lastName || undefined,
            email: email || undefined,
            phoneNumber: phone,
          },
        },
      });
      trackDeviceTransferToAdopt('dashboard_pet_row');
      showToast('Transfer initiated');
    } catch (err) {
      showToast(`Failed to transfer: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  async function handleTransferToFoster(
    petId: string,
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
  ) {
    try {
      await createFosterTransferMut({
        variables: {
          input: {
            petId,
            email,
            firstName,
            lastName: lastName || undefined,
            phoneNumber: phone || undefined,
          },
        },
      });
      trackDeviceTransferToFoster('dashboard_pet_row');
      showToast('Assigned to foster');
    } catch (err) {
      showToast(`Failed to transfer: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  async function handleRecallDevice(moduleId: string, petId: string) {
    try {
      await recallFosterTransferMut({ variables: { input: { petId } } });
      trackDeviceRecalled('dashboard_pet_row');
      showToast(`${moduleId} recalled to rescue`);
    } catch (err) {
      showToast(`Failed to recall: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  async function handleUnassignDevice(moduleId: string, petId: string) {
    try {
      await unassignDeviceMut({ variables: { moduleId, petId } });
      showToast(`Pet removed from ${moduleId}`);
    } catch (err) {
      showToast(`Failed to remove: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  function handleDeviceAction(actionKey: string, moduleId: string, petId: string) {
    if (actionKey === 'view') {
      history.push(`/rescue/devices?search=${encodeURIComponent(moduleId)}`);
    } else if (actionKey === 'transferAdopter') {
      adopterModal.show({ moduleId, petId });
    } else if (actionKey === 'transferFoster') {
      fosterModal.show({ moduleId, petId });
    } else if (actionKey === 'recallDevice') {
      handleRecallDevice(moduleId, petId);
    } else if (actionKey === 'removeFromPet') {
      handleUnassignDevice(moduleId, petId);
    }
  }

  async function handleChipAction(
    actionKey: string,
    chipId: string,
    shortId: string,
    serialNumber: string,
    petId: string,
  ) {
    if (actionKey === 'viewChip') {
      history.push(`/rescue/chips?search=${encodeURIComponent(shortId)}`);
    } else if (actionKey === 'addOwner') {
      const pet = allPets.find((p) => p.id === petId);
      if (!pet?.chip) return;
      // Construct the minimal Chip view-model that TransferChipModal renders
      // (id, petName, species, breed) plus _graphqlId for the submit mutation.
      addOwnerModal.show({
        _graphqlId: pet.chip.id,
        id: pet.chip.shortId,
        fullChipId: pet.chip.serialNumber,
        petName: pet.name,
        species: pet.species ?? null,
        breed: pet.breed?.name ?? null,
      } as Chip);
    } else if (actionKey === 'clearOwner') {
      try {
        // Reassign to reseller to get the ResellerChip ID, then recall to clear owner
        const { data } = await reassignToReseller({
          variables: { input: { serialNumber, clearOwnerInfo: false } },
        });
        const resellerChipId = data?.reassignChipToReseller?.id;
        if (resellerChipId) {
          await recallChipMut({ variables: { input: { chipId: resellerChipId } } });
        }
        showToast('Owner cleared');
      } catch (err) {
        showToast(`Failed to clear owner: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    }
  }

  async function handleAddOwnerSubmit(_chipId: string, adopter: AdopterFormData) {
    // TransferChipModal passes its display id back; the mutation needs the
    // GraphQL chip id, which we stashed on the modal data when opening.
    const graphqlChipId = addOwnerModal.data?._graphqlId;
    if (!graphqlChipId) return;
    try {
      await assignNewOwner({
        variables: {
          input: {
            chipId: graphqlChipId,
            contactInfo: mapAdopterFormToContactInfoInput(adopter),
          },
        },
      });
      trackChipOwnerAdded('dashboard_pet_row');
    } catch (err) {
      showToast(`Failed to add owner: ${err instanceof Error ? err.message : 'Unknown error'}`);
      throw err;
    }
  }

  // ── Analytics ──────────────────────────

  const tracked = useRef(false);
  useEffect(() => {
    if (!tracked.current && (chipStatsData || deviceStatsData)) {
      tracked.current = true;
      const total = pendingCount + completedCount;
      const rate = total > 0 ? Math.round((completedCount / total) * 100) : 0;
      trackDashboardViewed(actionItems.length, rate);
    }
  }, [chipStatsData, deviceStatsData]); // eslint-disable-line react-hooks/exhaustive-deps -- tracked.current guard ensures single fire

  return (
    <div>
      <h1>Dashboard</h1>

      <div className={styles.summaryCards}>
        <Link to="/rescue/chips" className={styles.summaryCard}>
          <div className={styles.cardLabel}>Fi Nano Microchips</div>
          <div className={styles.cardValue}>{chipStock + chipRegistered}</div>
          <div className={styles.cardSubtext}>
            {chipStock} in stock, {chipRegistered} registered
          </div>
        </Link>

        <Link to="/rescue/devices" className={styles.summaryCard}>
          <div className={styles.cardLabel}>GPS Devices</div>
          <div className={styles.cardValue}>{devicesCard.value}</div>
          {devicesCard.subtext && (
            <div
              className={styles.cardSubtext}
              style={devicesCard.subtextColor ? { color: devicesCard.subtextColor, fontWeight: 600 } : undefined}
            >
              {devicesCard.subtext}
            </div>
          )}
        </Link>

        <Link to="/rescue/gwa" className={styles.summaryCard}>
          <div className={styles.cardLabel}>GWA Redemptions</div>
          <div className={styles.cardValue}>{gwaCard.value}</div>
          {gwaCard.subtext && <div className={styles.cardSubtext}>{gwaCard.subtext}</div>}
        </Link>
      </div>

      {actionItems.length > 0 && (
        <div className={styles.contentCard}>
          <div className={styles.contentCardHeader}>
            <h2 className={styles.contentCardTitle}>Action Items</h2>
          </div>
          <div className={styles.actionItems}>
            {actionItems.map((item) => {
              const colors = URGENCY_COLORS[item.urgency];
              return (
                <Link
                  key={item.title}
                  to={item.link}
                  className={styles.actionItem}
                  onClick={() => trackActionItemClicked(item.title, item.urgency)}
                >
                  <div className={styles.actionIcon} style={{ background: colors.bg, color: colors.text }}>
                    {item.urgency === 'error' ? '!' : item.urgency === 'warning' ? '!' : 'i'}
                  </div>
                  <div className={styles.actionText}>
                    <div className={styles.actionTitle}>{item.title}</div>
                    <div className={styles.actionMeta}>{item.description}</div>
                  </div>
                  <div className={styles.actionChevron}>&rsaquo;</div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      <div className={styles.quickActionsSection}>
        <div className={styles.quickActionsLabel}>Quick actions</div>
        <div className={styles.quickActions}>
          <Link to="/rescue/chips" className={styles.quickActionBtnBlue}>
            Register Chips
          </Link>
          <button type="button" className={styles.quickActionBtnGreen} onClick={() => assignDeviceModal.show('')}>
            Assign Device
          </button>
          <button type="button" className={styles.quickActionBtnGreen} onClick={() => createPetModal.show()}>
            Create a New Pet
          </button>
          <Link to="/rescue/devices" className={styles.quickActionBtnYellow}>
            View Devices
          </Link>
          <Link to="/rescue/help" className={styles.quickActionBtnYellow}>
            Get Help
          </Link>
        </div>
      </div>

      <div className={styles.searchRow}>
        <SearchBar value={searchQuery} onChange={setSearchQuery} placeholder="Search by pet name or breed..." />
        <div className={styles.filterButtons}>
          <Button variant={statusFilter === null ? 'green' : 'ghost'} size="sm" onClick={() => setStatusFilter(null)}>
            All
          </Button>
          <Button
            variant={statusFilter === 'rescue' ? 'green' : 'ghost'}
            size="sm"
            onClick={() => setStatusFilter('rescue')}
          >
            Rescue
          </Button>
          <Button
            variant={statusFilter === 'foster' ? 'green' : 'ghost'}
            size="sm"
            onClick={() => setStatusFilter('foster')}
          >
            Foster
          </Button>
          <Button
            variant={statusFilter === 'adopted' ? 'green' : 'ghost'}
            size="sm"
            onClick={() => setStatusFilter('adopted')}
          >
            Adopted
          </Button>
          <Button
            variant={statusFilter === 'no_device' ? 'green' : 'ghost'}
            size="sm"
            onClick={() => setStatusFilter('no_device')}
          >
            No Device
          </Button>
        </div>
      </div>

      <PetsTable
        pets={filteredPets}
        loading={petsLoading}
        onAssignDevice={handleOpenAssignDevice}
        onViewPet={handleViewPet}
        onCreatePet={() => createPetModal.show()}
        onDeviceAction={handleDeviceAction}
        onChipAction={handleChipAction}
        onAddChip={(pet) => addChipModal.show(pet)}
      />

      <RemovePetModal
        open={removePetModal.open}
        onClose={removePetModal.close}
        pet={removePetModal.data}
        onConfirm={handleConfirmRemovePet}
      />

      <AssignDeviceModal
        key={assignDeviceModal.data ?? ''}
        open={assignDeviceModal.open}
        onClose={assignDeviceModal.close}
        initialPetId={assignDeviceModal.data ?? ''}
      />

      <AssignDeviceToPetModal
        open={assignToPetModal.open}
        onClose={assignToPetModal.close}
        pet={assignToPetModal.data}
      />

      <AddChipToPetModal open={addChipModal.open} onClose={addChipModal.close} pet={addChipModal.data} />

      <ViewPetModal
        open={viewPetModal.open}
        onClose={viewPetModal.close}
        pet={viewPetTarget}
        onAssignDevice={handleOpenAssignDevice}
        onAddChip={handleOpenAddChip}
        onRemovePet={handleOpenRemovePet}
        onEditPet={handleEditPet}
      />

      <PetFormModal mode="create" open={createPetModal.open} onClose={createPetModal.close} />

      <PetFormModal mode="edit" open={editPetModal.open} onClose={editPetModal.close} pet={editPetTarget} />

      <TransferToAdopterModal
        open={adopterModal.open}
        onClose={adopterModal.close}
        moduleId={adopterModal.data?.moduleId ?? ''}
        petId={adopterModal.data?.petId ?? ''}
        onTransfer={handleTransferToAdopter}
      />

      <TransferToFosterModal
        open={fosterModal.open}
        onClose={fosterModal.close}
        moduleId={fosterModal.data?.moduleId ?? ''}
        petId={fosterModal.data?.petId ?? ''}
        onTransfer={handleTransferToFoster}
      />

      <TransferChipModal
        open={addOwnerModal.open}
        onClose={addOwnerModal.close}
        chip={addOwnerModal.data}
        onTransfer={handleAddOwnerSubmit}
      />
    </div>
  );
}
